/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { CSSTransitionGroup } from 'react-transition-group';

import is from 'is_js';
import FlexBox from './FlexBox';
import { Tab } from './MiniSymbolsList';
import CalendarInput from './CalendarInput';

import withI18n from '../utils/withI18n';
import { getLabel } from '../utils/i18n';
import { TooltipContent } from './Tooltip';

const SEARCH_RANGE = 3; // months
const HINT_TIMEOUT = 2500; // 2.5sec

const TabContainer = styled(FlexBox)`
  height: 30px;
  padding-left: .5em;
  background:${({ theme }) => theme.filterbg};
  border-bottom: 1px solid #000;
`;

const TabContainerInner = styled(FlexBox)`
  margin: .3em 0;
  height: 2em;
  & button,
  & input {
    height: 22px;
  }
`;

const DateFilter = styled(FlexBox)`
  height: 30px;
  padding-left: 1em;
`;

const SearchTab = styled(Tab)`
  opacity: ${({ disabled }) => disabled ? '.5' : '1'};
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
`;

const TooltipContainer = styled(FlexBox)`
  position: relative;
`;

const StyledTooltipContent = styled(TooltipContent)`
  min-width: 150px;
  width: 150px;
  left: 0;
  bottom: 1em;
`;

const Hint = styled(FlexBox)`
  align-items: center;
  padding: .5em;
  right: 0;
`;

const mapStateToProps = ({
  isLoggedIn,
}) => ({
  isLoggedIn,
});

type Props = {
  symbol: string,
  isLoggedIn: boolean,
  i18n: Object,
  onFilterChange: Function,
  locale: string,
  period?: string,
}

type State = {
  manualStartDate: string,
  manualEndDate: string,
  overRangeHint: boolean,
}

export default
@withI18n
@connect(mapStateToProps)
class ProTableFilters extends React.Component<Props, State> {
  _timeout = undefined;

  static defaultProps = {
    i18n: {},
    isLoggedIn: false,
    onFilterChange: () => {},
    locale: 'en',
    period: '1d',
  }

  state = { manualStartDate: '', manualEndDate: '', overRangeHint: false }

  componentDidUpdate({ symbol }: Props, { overRangeHint }: State) {
    if (symbol !== this.props.symbol) {
      this._resetManualDates();
    }
    if (this.state.overRangeHint && overRangeHint !== this.state.overRangeHint) {
      this._timeout = setTimeout(() => {
        this.setState({ overRangeHint: false });
      }, HINT_TIMEOUT);
    }
  }

  componentWillUnmount() {
    clearTimeout(this._timeout);
  }

  _setTab = (ev: MouseEvent | null, period: string) => {
    if (ev && ev.target && period === this.props.period) return;
    const date = new Date();
    const end = date.setHours(24, 0, 0, 0);
    let start;
    switch (period) {
      case '3m':
        start = date.setMonth(date.getMonth() - 3);
        break;
      case '1m':
        start = date.setMonth(date.getMonth() - 1);
        break;
      case '1w':
        start = date.setDate(date.getDate() - 7);
        break;
      case '1d':
      default:
        start = date.setDate(date.getDate() - 1);
        break;
    }
    if (period !== '1d') start = date.setDate(date.getDate() - 1);

    this.props.onFilterChange(start, end, true, period);
  }

  _handleManualStartDateUpdate = (manualStartDate: string) => {
    const { manualEndDate } = this.state;
    if (!is.existy(manualStartDate) || !is.existy(manualEndDate)) return;
    const dateFrom = new Date(manualStartDate);
    const dateFromInMilliseconds = dateFrom.getTime();
    const dateToMilliseconds = new Date(manualEndDate).getTime();
    const limitInMilliseconds = dateFrom.setMonth(dateFrom.getMonth() + SEARCH_RANGE);
    if (dateToMilliseconds - dateFromInMilliseconds >
      limitInMilliseconds - dateFromInMilliseconds) {
      this.setState({
        manualEndDate: new Date(limitInMilliseconds).toString(),
        overRangeHint: true,
      });
    } else {
      this.setState({ overRangeHint: false });
    }
    this.setState({ manualStartDate });
  }

  _handleManualEndDateUpdate = (manualEndDate: string) => {
    const { manualStartDate } = this.state;
    if (!is.existy(manualStartDate) || !is.existy(manualEndDate)) return;
    const dateFromInMilliseconds = new Date(manualStartDate).getTime();
    const dateTo = new Date(manualEndDate);
    const dateToMilliseconds = dateTo.getTime();
    const limitInMilliseconds = dateTo.setMonth(dateTo.getMonth() - SEARCH_RANGE);

    if (dateToMilliseconds - dateFromInMilliseconds >
      dateToMilliseconds - limitInMilliseconds) {
      this.setState({
        manualStartDate: new Date(limitInMilliseconds).toString(),
        overRangeHint: true,
      });
    } else {
      this.setState({ overRangeHint: false });
    }
    this.setState({ manualEndDate });
  }

  _search = () => {
    const { manualStartDate, manualEndDate } = this.state;
    const { isLoggedIn } = this.props;
    if (manualStartDate === '' || manualEndDate === '' || !isLoggedIn) return;
    this.props.onFilterChange(
      new Date(manualStartDate).getTime(),
      new Date(manualEndDate).getTime(),
      false
    );
  }

  _resetManualDates() {
    this.setState({ manualStartDate: '', manualEndDate: '', overRangeHint: false });
  }

  render() {
    const {
      i18n, symbol, period,
    } = this.props;
    const { manualStartDate, manualEndDate, overRangeHint } = this.state;
    return (
      <TabContainer flex="1 0 100%" align="center" justify="space-between">
        <FlexBox>
          <TabContainerInner>
            <Tab selected={period === '1d'} onClick={(ev) => this._setTab(ev, '1d')}>{getLabel('exchange-pro-day', i18n, '1 Day')}</Tab>
            <Tab selected={period === '1w'} onClick={(ev) => this._setTab(ev, '1w')}>{getLabel('exchange-pro-week', i18n, '1 Week')}</Tab>
            <Tab selected={period === '1m'} onClick={(ev) => this._setTab(ev, '1m')}>{getLabel('exchange-pro-month', i18n, '1 Month')}</Tab>
            <Tab selected={period === '3m'} onClick={(ev) => this._setTab(ev, '3m')}>{getLabel('exchange-pro-quarter', i18n, '3 Months')}</Tab>
          </TabContainerInner>
          <DateFilter align="center">
            {getLabel('exchange-pro-startAndEndDate', i18n, 'From')}&nbsp;
            <CalendarInput
              symbol={symbol}
              callbackFn={this._handleManualStartDateUpdate}
              passedDate={manualStartDate === '' ? null : new Date(manualStartDate)}
              tooltipOpen={overRangeHint}
              maxDateStr={manualEndDate}
            />
              &nbsp;&nbsp;
            <CalendarInput
              symbol={symbol}
              callbackFn={this._handleManualEndDateUpdate}
              passedDate={manualEndDate === '' ? null : new Date(manualEndDate)}
              minDateStr={manualStartDate}
            />
            <SearchTab onClick={this._search}>
              {getLabel('exchange-trade-search', i18n, 'Search')}
            </SearchTab>
          </DateFilter>
          <CSSTransitionGroup
            transitionName="tooltip"
            transitionAppear={true}
            transitionEnterTimeout={HINT_TIMEOUT}
            transitionLeaveTimeout={500}
            transitionAppearTimeout={HINT_TIMEOUT}
            component={Hint}
          >
            {overRangeHint ? (
              <TooltipContainer>
                <StyledTooltipContent isOnTop={true}>
                  {
                    getLabel(
                      'exchange-proTableFilters-outOfsearchRangeHint',
                      i18n,
                      'We only support you search the orders in 3 months.',
                    )
                  }
                </StyledTooltipContent>
              </TooltipContainer>
            ) : null}
          </CSSTransitionGroup>
        </FlexBox>
      </TabContainer>
    );
  }
}
